'use client';
import { useTranslation, useWindowDimensions } from '@/hook';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  GridContainer,
  GridItem,
  GridItemIcon,
  GridItemMainText,
  GridItemSecondaryText,
  GridSectionFooter,
} from '../StyledComponents/grid-styled-components';
import {
  CarProfileSectionHeaderContainer,
  CarProfileSectionHeaderTitle,
  CarProfileSectionViewMore,
  CarProfileSectionViewMoreNoBorder,
  CarProfileViewMoreContainer,
  CarProfileViewMoreContainerMobile,
} from '../StyledComponents/section-header-components';

export enum EGGRID_ITEM_VARIANT {
  TEXT = 'text',
  TEXT_WITH_ICON = 'text-with-icon',
}

const GridSection = ({
  gridItems,
  gridItemVariant,
  sectionHeader,
  viewAll,
  gridItemClickable,
  gridItemClicked,
}: {
  gridItems: { [key: string]: string };
  gridItemVariant: EGGRID_ITEM_VARIANT;
  sectionHeader: string;
  viewAll?: string;
  gridItemClickable?: boolean;
  gridItemClicked?: (id: string, isViewAll: boolean) => void;
}) => {
  const { t, i18n, IS_RTL } = useTranslation();
  const { width, IS_DESKTOP } = useWindowDimensions();
  const lang = i18n.language;
  const itemClicked = (id: string, isViewAll: boolean) => {
    if (!gridItemClicked) return;
    gridItemClicked(id, isViewAll);
  };

  const determinePosition = (index: number, length: number, totalItemsCount: number) => {
    const borderPositions = {
      top: false,
      bottom: false,
      left: false,
      right: false,
    };
    if (IS_DESKTOP) {
      // for first row
      if (index <= length) {
        borderPositions.bottom = true;
      }
      //for first row excluding the last grid item
      if (index < length) {
        borderPositions.right = true;
      }
      //for first row last grid item
      if (index === length) {
        borderPositions.right = IS_RTL;
      }
      if (index === 0) {
        borderPositions.right = !IS_RTL;
      }
      if (index === length + 1) {
        borderPositions.right = !IS_RTL;
      }
      //for second row excluding the last grid item
      if (index > length + 1 && index < totalItemsCount - 1) {
        borderPositions.right = true;
      }
      //for second row last grid item
      if (index === totalItemsCount - 1) {
        borderPositions.right = IS_RTL;
      }
      return borderPositions;
    }

    // MOBILE VIEW

    // for first column
    if ((index + 1) % 2 !== 0) {
      borderPositions.right = !IS_RTL;
      borderPositions.left = IS_RTL;
    }
    //for all rows excluding the last one
    if (index + 2 < totalItemsCount) {
      borderPositions.bottom = true;
    }
    return borderPositions;
  };
  const gridItemsKeys = Object.keys(gridItems);
  const createGrid = () => {
    const itemCount = gridItemsKeys.length;
    const length = gridItemsKeys.length / 2 - 1;
    const itemWidth = 100 / (length + 1);
    const gridArray = [];
    if (gridItemVariant === 'text-with-icon') {
      for (let i = 0; i < itemCount; i += 1) {
        const position = determinePosition(i, length, itemCount);
        const item = (
          <GridItem
            key={gridItemsKeys[i]}
            onClick={gridItemClickable ? () => itemClicked(gridItemsKeys[i], false) : () => null}
            cliclable={gridItemClickable}
            itemWidth={IS_DESKTOP ? itemWidth : 0}
            variant={gridItemVariant}
            {...position}
          >
            {gridItemVariant === 'text-with-icon' && (
              <GridItemIcon src={gridItems[gridItemsKeys[i]]} alt="" />
            )}
            {/*
      @eslint-disable-next-line @typescript-eslint/ban-ts-comment
      @ts-ignore */}
            <GridItemMainText varinat={'captionMedium'}>{t(gridItemsKeys[i])}</GridItemMainText>
          </GridItem>
        );
        gridArray.push(item);
      }
      return [...gridArray];
    }
    for (let i = 0; i < itemCount; i += 1) {
      const position = determinePosition(i, length, itemCount);
      const item = (
        <GridItem
          key={gridItemsKeys[i]}
          onClick={gridItemClickable ? () => itemClicked(gridItemsKeys[i], false) : () => null}
          cliclable={gridItemClickable}
          itemWidth={IS_DESKTOP ? itemWidth : 0}
          variant={gridItemVariant}
          {...position}
        >
          {/*
    @eslint-disable-next-line @typescript-eslint/ban-ts-comment
    @ts-ignore */}
          <GridItemSecondaryText varinat={'caption'}>{t(gridItemsKeys[i])}</GridItemSecondaryText>

          {/*
    @eslint-disable-next-line @typescript-eslint/ban-ts-comment
    @ts-ignore */}
          <GridItemMainText varinat={'captionMedium'}>
            {t(gridItems[gridItemsKeys[i]] as unknown as TemplateStringsArray)}
          </GridItemMainText>
        </GridItem>
      );
      gridArray.push(item);
    }
    return [...gridArray];
  };
  return (
    <>
      <CarProfileSectionHeaderContainer>
        <CarProfileSectionHeaderTitle variant="subtitle1Bold">
          {sectionHeader}
        </CarProfileSectionHeaderTitle>
        {viewAll && !IS_DESKTOP && (
          <CarProfileSectionViewMoreNoBorder
            onClick={gridItemClickable ? () => itemClicked(gridItemsKeys[0], true) : () => null}
          >
            <CarProfileSectionViewMore>{viewAll}</CarProfileSectionViewMore>
            {lang === 'ar' ? (
              <ChevronLeft sx={{ color: '#00673E' }} />
            ) : (
              <ChevronRight sx={{ color: '#667085' }} />
            )}
          </CarProfileSectionViewMoreNoBorder>
        )}
      </CarProfileSectionHeaderContainer>
      <GridContainer>
        {createGrid()}
        {viewAll && IS_DESKTOP && (
          <GridSectionFooter>
            <CarProfileViewMoreContainer
              onClick={gridItemClickable ? () => itemClicked(gridItemsKeys[0], true) : () => null}
            >
              <CarProfileSectionViewMore variant="captionMedium">
                {viewAll}
              </CarProfileSectionViewMore>
              {lang === 'ar' ? (
                <ChevronLeft sx={{ color: '#00673E' }} />
              ) : (
                <ChevronRight sx={{ color: '#667085' }} />
              )}
            </CarProfileViewMoreContainer>
          </GridSectionFooter>
        )}
      </GridContainer>
      {viewAll && !IS_DESKTOP && (
        <CarProfileViewMoreContainerMobile
          onClick={gridItemClickable ? () => itemClicked(gridItemsKeys[0], true) : () => null}
        >
          <CarProfileSectionViewMore>{viewAll}</CarProfileSectionViewMore>
          {lang === 'ar' ? (
            <ChevronLeft sx={{ color: '#00673E' }} />
          ) : (
            <ChevronRight sx={{ color: '#667085' }} />
          )}
        </CarProfileViewMoreContainerMobile>
      )}
    </>
  );
};
export default GridSection;
